import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ImageGenService,
  IUpdateImagePayload,
} from 'features/text-to-image-generator/services/image-gen.api';
import { Download, MoreVertical, PlusIcon, Trash, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import EditImageModal from '../components/modals/EditImageModel';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import CardSkeleton from '../components/Skeletons/CardSkeleton';
import LoadingSkeleton from '../components/Skeletons/LoadingSkeleton';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { FullScreenImage } from 'components/ui/full-screen-image';

// Types
interface Image {
  id: number;
  img_url: string;
  prompt_input: string;
  prompt_type: string;
  created_at: string;
}

interface ImageCardProps {
  item: Image;
  index: number;
  onHover: (index: number | null) => void;
  onDelete: (id: number) => Promise<void>;
  onUpdate?: (
    e: React.FormEvent,
    id: number,
    payload: { prompt_input: string; prompt_type: string }
  ) => Promise<void>;
}

// Constants
const RECAPTCHA_ACTION = 'KaiRecaptcha0911';
const DEFAULT_TOKEN = 'default_token';

// Utility functions
const handleDownloadImage = async (
  imageUrl: string,
  fileName: string,
  format: string = 'png'
) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    // Convert blob to desired format if needed
    let downloadBlob = blob;
    if (blob.type !== `image/${format}`) {
      const img = new Image();
      const canvas = document.createElement('canvas');
      img.src = URL.createObjectURL(blob);

      await new Promise((resolve) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0);
          canvas.toBlob((newBlob) => {
            if (newBlob) {
              downloadBlob = newBlob;
            }
            resolve(null);
          }, `image/${format}`);
        };
      });
    }

    const downloadUrl = window.URL.createObjectURL(downloadBlob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `${fileName}.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
    toast.success('Download complete');
  } catch (error) {
    console.error('Download failed:', error);
    toast.error('Download failed');
  }
};

// Add DownloadModal component before ImageCard component
const DownloadModal = ({
  isOpen,
  onClose,
  onDownload,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (format: string) => void;
}) => {
  const [selectedFormat, setSelectedFormat] = useState('png');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onDownload(selectedFormat);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed z-30 inset-0 bg-black/20" onClick={onClose} />
      <div className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background text-text-primary rounded-xl shadow-lg p-6 w-[90%] max-w-md">
        <h3 className="text-lg font-semibold mb-4">Export As</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className=" flex gap-4 justify-center items-center">
            <div className="gap-2 flex items-center justify-center">
              <input
                type="radio"
                name="format"
                value="png"
                checked={selectedFormat === 'png'}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="form-radio"
              />
              <span>PNG</span>
            </div>
            <div className="gap-2 flex items-center justify-center">
              <input
                type="radio"
                name="format"
                value="jpg"
                checked={selectedFormat === 'jpg'}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="form-radio"
              />
              <span>JPG</span>
            </div>
            <div className="gap-2 flex items-center justify-center">
              <input
                type="radio"
                name="format"
                value="jpeg"
                checked={selectedFormat === 'jpeg'}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="form-radio"
              />
              <span>JPEG</span>
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <Button
              variant={'customSecondary'}
              onClick={onClose}
              className="bg-secondary"
            >
              Cancel
            </Button>
            <Button variant={'customSecondary'} className="bg-secondary">
              Download
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

// ImageCard Component
const ImageCard: React.FC<ImageCardProps> = ({
  item,
  index,
  onHover,
  onDelete,
  onUpdate,
}) => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const formattedDate = new Date(item.created_at).toLocaleDateString();

  return (
    <div
      className="relative p-2 bg-background text-text-primary rounded-md w-full max-w-[300px] mx-auto h-auto group"
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
    >
      {/* On desktop, we use regular img without fullscreen capability */}
      <div className="h-48 sm:h-56 md:h-64 mb-2 hidden sm:block">
        <img
          src={item.img_url}
          alt={`Generated image ${item.id}`}
          className="rounded-md object-cover w-full h-full"
        />
      </div>

      {/* On mobile, we use FullScreenImage component */}
      <div className="h-48 sm:h-56 md:h-64 mb-2 sm:hidden">
        <FullScreenImage
          src={item.img_url}
          alt={`Generated image ${item.id}`}
          imageClassName="rounded-md object-cover w-full h-[12rem]"
          modalClassName="bg-transparent"
        />
      </div>

      <div className="px-2 flex justify-between items-center">
        <div className="w-[calc(100%-30px)]">
          <h3 className="text-lg font-medium w-full truncate">
            {item.prompt_input || 'Untitled Image'}
          </h3>
          <p className="text-sm ">{formattedDate}</p>
        </div>
        <div className="flex items-center sm:hidden">
          <button className="p-1 text-primary hover:text-white transition-colors">
            <Menu item={item} onUpdate={onUpdate} onDelete={onDelete} />
          </button>
        </div>
      </div>

      <div className="absolute hidden top-2 left-2 right-2 h-64 bg-black/50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 sm:flex items-center justify-center gap-4">
        <button className="p-2 text-white rounded-full hover:bg-white/20 transition-colors">
          <EditImageModal
            item={{
              prompt_input: item.prompt_input,
              prompt_type: item.prompt_type,
            }}
            id={item.id}
            handleUpdate={onUpdate}
          />
        </button>
        <button
          className="p-2 text-white rounded-full hover:bg-white/20 transition-colors"
          onClick={() => setIsDownloadModalOpen(true)}
        >
          <Download size={20} />
        </button>
        <button
          className="p-2 text-white rounded-full hover:bg-white/20 transition-colors"
          onClick={() => onDelete(item.id)}
        >
          <Trash2 size={20} />
        </button>
      </div>

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onDownload={(format) =>
          handleDownloadImage(item.img_url, item.prompt_input, format)
        }
      />
    </div>
  );
};

const Menu = ({
  item,
  onUpdate,
  onDelete,
}: {
  item: Image;
  onUpdate?: (
    e: React.FormEvent,
    id: number,
    payload: { prompt_input: string; prompt_type: string }
  ) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <>
      <button
        onClick={toggleMenu}
        className="p-2 hover:bg-gray-100 rounded-full"
      >
        <MoreVertical className="text-primary h-6 w-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed z-30 inset-0 bg-black/20"
            onClick={toggleMenu}
          />
          <div className="fixed z-50 bottom-0 left-0 right-0 bg-background-card text-text-primary rounded-t-xl shadow-lg">
            <div className="w-12 h-1 rounded-full mx-auto my-3" />
            <div className="p-4 space-y-4">
              <FullScreenImage
                src={item.img_url}
                alt={`Generated image ${item.id}`}
                className="w-full"
              >
                <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg">
                  <span className="text-primary h-5 w-5">👁️</span>
                  <span>View Image</span>
                </button>
              </FullScreenImage>
              <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
                <EditImageModal
                  item={{
                    prompt_input: item.prompt_input,
                    prompt_type: item.prompt_type,
                  }}
                  id={item.id}
                  handleUpdate={onUpdate}
                />{' '}
              </button>
              <button
                className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  setIsDownloadModalOpen(true);
                  setIsOpen(false);
                }}
              >
                <Download className="text-primary h-5 w-5" />
                <span>Export</span>
              </button>
              <button
                className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  onDelete(item.id);
                  setIsOpen(false);
                }}
              >
                <Trash className="text-indigo-500 h-5 w-5" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </>
      )}

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onDownload={(format) => {
          handleDownloadImage(item.img_url, item.prompt_input, format);
          setIsDownloadModalOpen(false);
        }}
      />
    </>
  );
};

// Main TextToImage Component
const TextToImage: React.FC = () => {
  const queryClient = useQueryClient();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) return null;
    return executeRecaptcha(RECAPTCHA_ACTION);
  }, [executeRecaptcha]);

  const { data, isLoading } = useQuery({
    queryKey: ['user-image'],
    queryFn: ImageGenService.getUserImages,
  });

  const deleteImage = useMutation({
    mutationKey: ['user-image-delete'],
    mutationFn: ImageGenService.deleteImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-image'] });
      toast.success('Image deleted successfully');
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete image');
    },
  });

  const updateImage = useMutation({
    mutationKey: ['user-image-edit'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: IUpdateImagePayload;
    }) => ImageGenService.updateImage(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-image'] });
      toast.success('Image updated successfully');
    },
    onError: (error) => {
      console.error('Update error:', error);
      toast.error('Failed to update image details');
    },
  });

  const handleDelete = async (id: number): Promise<void> => {
    await deleteImage.mutateAsync(id);
  };

  const handleUpdate = async (
    e: React.FormEvent,
    id: number,
    payload: { prompt_input: string; prompt_type: string }
  ): Promise<void> => {
    e.preventDefault();
    const token = await handleReCaptchaVerify();

    await updateImage.mutateAsync({
      id,
      payload: {
        input: payload.prompt_input,
        prompt_type: payload.prompt_type,
        token: token || DEFAULT_TOKEN,
      },
    });
  };

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="mx-auto rounded-lg rounded-t-none p-4 lg:p-16 w-full max-w-[1400px] text-text-primary">
      <div className="flex flex-col lg:flex-row w-full justify-between items-start lg:items-center gap-4 lg:gap-0">
        <div className="w-full lg:w-auto">
          <h2 className="text-[20px] lg:text-[24px] font-bold">Your Images</h2>
          <p className="text-[12px] lg:text-[14px]  mt-1">
            All your images are safely stored here in one place. Easily view,
            organize, and manage your them here.
          </p>
        </div>
        <div className="w-full lg:w-auto">
          <div className="hidden sm:flex gap-1 cursor-pointer text-primary items-center justify-start lg:justify-end">
            <PlusIcon size={20} />
            <div
              className="underline text-[14px] lg:text-[16px]"
              onClick={() => navigate('/text-to-image/get-started')}
            >
              Generate New Images
            </div>
          </div>
        </div>
      </div>
      {data && data.data?.length == 0 ? (
        <div className="w-full">
          <LoadingSkeleton text="Images" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 my-7">
          {isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            data?.data?.length &&
            data?.data.map((item, index) => (
              <ImageCard
                key={item.id}
                item={item}
                index={index}
                onHover={() => {}}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TextToImage;
